/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/suppliers-procurement.md and
 * operations.md Marketing Constraints.
 *
 * PO lifecycle has 7 states per suppliers-procurement.md (authoritative):
 *   DRAFT → SENT → CONFIRMED → SHIPPED → PARTIALLY_RECEIVED → DELIVERED → COMPLETED
 * Only DRAFT is editable. Receiving is atomic via increment_received_quantity
 * RPC (do NOT name the RPC).
 *
 * XOR scope: each PO links to exactly one project OR one service
 * (purchase_orders_entity_xor CHECK constraint). project_id and service_id
 * are both nullable, exactly one is set.
 *
 * PO line items link to estimate line items via
 * purchase_order_line_items.estimate_line_item_id for cost_type inheritance
 * (do NOT reveal the FK column name).
 *
 * On receive, cost_type is inherited from the linked estimate line item via
 * lineItemTypeToCostType() (do NOT reveal the helper function name).
 *
 * Pricing signals flywheel captures actual_variance on receive — describe
 * as "CC learns from the gap between estimated and actual costs," do not
 * name the service file or reveal the fire-and-forget implementation.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  ShoppingCart,
  Truck,
  FileCheck,
  Lock,
  GitBranch,
  TrendingUp,
  Package,
  Layers,
  Briefcase,
  DollarSign,
  Receipt,
  ShieldCheck,
  Hammer,
  Calculator,
  Printer,
  Sparkles,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Purchase Orders",
  title: "Procurement that's wired",
  titleHighlight: "into every estimate, project, and actual cost.",
  description:
    "Control purchasing from draft through delivery, tie receipts back to job costs, and use real buying outcomes to improve future estimating decisions.",

  featureGrid: {
    title: "Procurement built for construction, not e-commerce",
    items: [
      {
        icon: ShoppingCart,
        title: "7-State Lifecycle",
        description:
          "Draft → Sent → Confirmed → Shipped → Partially Received → Delivered → Completed. Every transition is validated by workflow rules. You always know what's happening with every order.",
      },
      {
        icon: Lock,
        title: "Only DRAFT Is Editable",
        description:
          "Once a PO is sent, it's locked. No silent edits, no retroactive quantity changes after the fact. Want to change a sent PO? Cancel it and create a new draft so history stays clean.",
      },
      {
        icon: Truck,
        title: "Partial Delivery Tracking",
        description:
          "Track partial deliveries across multiple receipts so your team always knows what has arrived, what is pending, and what is back-ordered.",
      },
      {
        icon: GitBranch,
        title: "Project or Service Scope",
        description:
          "Each PO is tied to one clear scope so cost attribution stays clean and operational ownership is obvious.",
      },
      {
        icon: Sparkles,
        title: "Generate From Estimate",
        description:
          "One click to draft POs from the material lines on your estimate. Quantities, unit costs, and supplier assignments flow through. You review, edit, and send.",
      },
      {
        icon: Layers,
        title: "PO Lines Link to Estimate Lines",
        description:
          "Connect purchase lines to estimate context so received costs roll into the right financial bucket.",
      },
      {
        icon: FileCheck,
        title: "Record Bill Flow",
        description:
          "When supplier bills arrive, record them against the PO so project and service cost tracking stays clean.",
      },
      {
        icon: TrendingUp,
        title: "Your Cost Data Gets Sharper Over Time",
        description:
          "When the received unit cost differs from the estimated unit cost, CC captures the variance. Over time your cost database gets sharper — you estimate closer to reality on every future bid.",
      },
      {
        icon: Printer,
        title: "PDF Export",
        description:
          "Export any PO as a branded PDF in one click. Clean layout that looks like a real document, not a spreadsheet export.",
      },
    ],
  },

  workflow: {
    title: "What a purchase order actually looks like in CC",
    description:
      "A practical six-step flow from PO creation to cost attribution and learning.",
    steps: [
      {
        label: "Draft",
        title: "Create the PO — from scratch or from the estimate",
        description:
          "Build purchase orders directly from active work context so ordering decisions are faster and more consistent.",
        details: [
          "From-scratch and estimate-assisted creation paths",
          "Supplier and quantity review before send",
          "Linkage to relevant estimating context",
          "Project/service scope clarity",
          "Consistent PO numbering and tracking",
        ],
      },
      {
        label: "Send",
        title: "Send to the supplier and lock the record",
        description:
          "Send clean purchase records and preserve ordering integrity once the PO leaves draft.",
        details: [
          "Lifecycle status progression support",
          "Post-send edit protections",
          "PDF output for supplier communications",
          "Email or manual send options",
          "Audit-friendly send history",
        ],
      },
      {
        label: "Confirm",
        title: "Supplier confirms the order",
        description:
          "Track supplier acknowledgment so your team can separate pending outreach from committed procurement.",
        details: [
          "Confirmed-state visibility",
          "Clear distinction between sent and accepted",
          "Stronger purchasing follow-through",
        ],
      },
      {
        label: "Ship & Receive",
        title: "Track partial deliveries without double-counting",
        description:
          "Record deliveries as they arrive, including partials, so field and office teams share the same receiving reality.",
        details: [
          "Partial and full receipt tracking",
          "Reliable quantity updates even when multiple team members receive the same order",
          "Delivery-state visibility",
          "Overage/shortage awareness",
          "Receipt timeline context",
        ],
      },
      {
        label: "Cost Attribution",
        title: "Receipts become actual costs in the right category",
        description:
          "Keep purchasing and cost tracking connected so received materials and services show up in the right financial context.",
        details: [
          "Estimate-informed cost categorization support",
          "Project/service-aware actual cost flow",
          "Faster budget variance visibility",
          "Less manual categorization work",
          "Cleaner cost history for review",
        ],
      },
      {
        label: "Pricing Signal",
        title: "The variance feeds a learning loop",
        description:
          "Use purchasing variance to continuously refine estimating assumptions without adding extra admin work.",
        details: [
          "Estimate-vs-actual variance capture",
          "Category-level learning support",
          "Cost database refinement loop",
          "Review visibility for procurement trends",
          "Compounding estimate quality over time",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: ShoppingCart,
      title: "A 7-State Lifecycle Built For How POs Actually Work",
      description:
        "Track each order from draft to completion with clear lifecycle visibility so teams always know what has been ordered, shipped, or received.",
      bullets: [
        "Seven-state procurement lifecycle visibility",
        "Clear transition history",
        "Post-send record integrity",
        "Partial delivery support",
        "Cleaner close-out and reconciliation flow",
      ],
    },
    {
      icon: Truck,
      title: "Reliable Receiving — No Double-Counting",
      description:
        "Record receipts confidently, including partial deliveries, with safeguards that reduce quantity and reconciliation errors.",
      bullets: [
        "Safer receiving under concurrent team activity",
        "Partial and back-order tracking support",
        "Overage awareness during receipt",
        "Line-level receipt history",
        "Improved inventory and cost confidence",
      ],
    },
    {
      icon: Layers,
      title: "From Estimate to PO to Actual Cost — One Pipeline",
      description:
        "Keep estimating, purchasing, and actual-cost reporting connected so financial visibility improves as work is delivered.",
      bullets: [
        "Estimate-linked purchasing context",
        "Smarter cost attribution at receipt time",
        "Project/service-aware cost flow",
        "Faster budget variance visibility",
        "Less manual recategorization work",
      ],
    },
    {
      icon: TrendingUp,
      title: "Get Sharper With Every Job",
      description:
        "Capture estimate-vs-actual buying variance and use it to make future pricing decisions more accurate over time.",
      bullets: [
        "Automatic variance capture support",
        "Category-level trend visibility",
        "Variance capture runs alongside receiving with no extra steps",
        "Continuous estimating refinement",
        "Compounding pricing accuracy",
      ],
    },
  ],

  connections: {
    title: "How purchase orders connect to the rest of CC",
    items: [
      {
        icon: Calculator,
        flow: "Estimate → Draft PO",
        description:
          "Generate draft POs from estimate material lines in one click. Supplier, quantity, and unit cost flow through — you review and send.",
      },
      {
        icon: Briefcase,
        flow: "PO Receive → Project / Service Actual Cost",
        description:
          "Receiving a PO updates actual costs on the related project or service so budget tracking reflects real buying activity.",
      },
      {
        icon: DollarSign,
        flow: "Receipt → Budget Rollup",
        description:
          "Received costs become part of project and service cost records, improving financial review and estimate-vs-actual analysis.",
      },
      {
        icon: Hammer,
        flow: "Bill → Supplier Reconciliation",
        description:
          "Record supplier bills against each PO to tighten reconciliation and reduce month-end cleanup.",
      },
      {
        icon: Package,
        flow: "Variance → Pricing Signals",
        description:
          "The gap between estimated and actual unit cost feeds a pricing intelligence loop that sharpens your cost database over time.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Suppliers & Subcontractors",
      href: "/features/subcontractors",
      icon: Receipt,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Change Orders",
      href: "/features/change-orders",
      icon: ShieldCheck,
    },
    {
      label: "Cost Database",
      href: "/features/cost-database",
      icon: Package,
    },
  ],
};

export function PurchaseOrdersPageClient() {
  return <FeaturePageTemplate config={config} />;
}
